import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Divider,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Trash2, PlusCircleIcon } from 'lucide-react';
import InputField from '@/components/InputField/InputField';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    createAddressServicesSchema,
    type AddressServicesInputs,
    isCellNumberRequired,
} from '../schemas/validationSchemas';
import { getIndigentTenantRules } from '../tenant/indigentTenantRules';
import { validateFile, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_MB } from '@/utils/fileValidation';
import { locationService, type Ward, type Town } from '@/services/locationService';
import { useFilePreview } from '@/hooks/useFilePreview';
import FilePreviewDialog from '@/components/FilePreviewDialog/FilePreviewDialog';
import { indigentValidationService } from '@/services/indigentValidationService';
import { useResponsiveToast } from '@/components/Toast';
import { sanitizeMoneyInput } from '@/utils/moneyInput';

// Utility function to format text with proper capitalization
const formatTextWithCapitalization = (text: string): string => {
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

type StepProps = {
    formData: AddressServicesInputs;
    setFormData: (newData: Partial<AddressServicesInputs>) => void;
    onValidationChange: (isValid: boolean) => void;
    mode?: 'create' | 'edit';
    financialYear?: string; // Add financial year prop
    isEditable?: boolean;
    indigentNumber?: string; // Add indigent number prop
};

export interface StepAddressServicesRef {
    validateAccountMeterCombination: () => Promise<boolean>;
    isValidating: boolean;
    runValidation: () => Promise<boolean>;
}

const StepAddressServices = React.forwardRef<StepAddressServicesRef, StepProps>(({
    formData,
    setFormData,
    onValidationChange,
    mode = 'create',
    financialYear,
    isEditable = true,
    indigentNumber,
}, ref) => {
    const isInitialized = useRef(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isOpen, filePath, fileData, filename, openPreview, openPreviewWithData, closePreview } = useFilePreview();
    const { error: showError } = useResponsiveToast();
    const [isValidating, setIsValidating] = useState(false);
    
    const cellNumberRequired = isCellNumberRequired();
    const tenantRules = useMemo(() => getIndigentTenantRules(), []);
    const addressServicesSchema = useMemo(
        () => createAddressServicesSchema(tenantRules),
        [tenantRules],
    );

    // State for wards and towns data
    const [wards, setWards] = useState<Ward[]>([]);
    const [towns, setTowns] = useState<Town[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const {
        control,
        watch,
        formState: { errors, isValid },
        setValue,
        getValues,
    } = useForm<AddressServicesInputs>({
        resolver: zodResolver(addressServicesSchema),
        defaultValues: formData,
        mode: 'onChange',
    });

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: 'occupants',
    });

    // Function to validate Account Number and Electricity Meter Number combination
    const validateAccountMeterCombination = useCallback(async (accountNumber: string, electricityMeterNo: string, financialYear: string): Promise<boolean> => {
        try {
            setIsValidating(true);
            
            const response = await indigentValidationService.validateAccountMeter(accountNumber, electricityMeterNo, financialYear, indigentNumber);
            
            if (!response.account_number_valid || !response.electricity_meter_valid) {
                let errorMessage = '';
                if (!response.account_number_valid && !response.electricity_meter_valid) {
                    errorMessage = 'Both Account Number and Electricity Meter Number already exist for this Financial Year.';
                } else if (!response.account_number_valid) {
                    errorMessage = 'Account Number already exists for this Financial Year.';
                } else if (!response.electricity_meter_valid) {
                    errorMessage = 'Electricity Meter Number already exists for this Financial Year.';
                }
                showError(errorMessage);
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Validation error:', error);
            showError('Error validating Account Number and Electricity Meter Number. Please try again.');
            return false;
        } finally {
            setIsValidating(false);
        }
    }, [indigentNumber, showError]);

    // Expose validation function to parent component
    React.useImperativeHandle(ref, () => ({
        validateAccountMeterCombination: () => {
            const currentValues = getValues();
            if (currentValues.accountNumber && currentValues.electricityMeterNo && financialYear) {
                return validateAccountMeterCombination(currentValues.accountNumber, currentValues.electricityMeterNo, financialYear);
            }
            return Promise.resolve(true);
        },
        isValidating,
        runValidation: () => {
            // force react-hook-form validation pass
            return new Promise<boolean>(resolve => {
                // trigger is not imported here; use isValid recalculation via setValue on same values
                const values = getValues();
                // noop set to cause validation run where needed
                Object.entries(values).forEach(([key, value]) => {
                    setValue(key as any, value as any, { shouldValidate: true, shouldTouch: true });
                });
                // The resolver runs synchronously; give a tick for state to settle
                setTimeout(() => {
                    resolve(true);
                }, 0);
            });
        },
    }), [getValues, isValidating, validateAccountMeterCombination, financialYear, setValue]);

    // Watch form changes
    const formValues = watch();

    // Fetch wards and towns data on component mount
    useEffect(() => {
        const fetchLocationData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch both wards and towns in parallel
                const [wardsData, townsData] = await Promise.all([
                    locationService.getWards(),
                    locationService.getTowns(),
                ]);
                
                setWards(wardsData);
                setTowns(townsData);
            } catch (err) {
                console.error('Error fetching location data:', err);
                setError('Failed to load location data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchLocationData();
    }, []);

    // Initialize form with existing data (for edit mode) - only once
    useEffect(() => {
        if (!isInitialized.current && formData.occupants && formData.occupants.length > 0) {
            // Replace existing occupants with the ones from formData
            replace(formData.occupants);
            isInitialized.current = true;
        }
    }, [formData.occupants, replace]);

    // Watch for form changes and update parent - but prevent infinite loops
    useEffect(() => {
        const subscription = watch(value => {
            // Only update if the values are actually different to prevent loops
            if (JSON.stringify(value) !== JSON.stringify(formData)) {
                setFormData(value as AddressServicesInputs);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setFormData, formData]);

    useEffect(() => {
        onValidationChange(isValid);
    }, [isValid, onValidationChange]);

    const handleAddOccupant = () => {
        append({
            name: '',
            surname: '',
            personalId: '',
            employerName: '',
            monthlyIncome: '',
            cellNumber: '',
        });
    };

    const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const validationResult = validateFile(file);
        if (!validationResult.isValid) {
            showError(validationResult.error?.message || 'Invalid file');
            return;
        }

        // Create a new array with the correct length
        const currentPhotos = formValues.occupantPhotos || [];
        const updatedPhotos = [...currentPhotos];

        // Ensure the array is long enough
        while (updatedPhotos.length <= index) {
            updatedPhotos.push(undefined);
        }

        // Add new photo
        updatedPhotos[index] = file;

        // Update form data
        const newFormData = {
            ...formValues,
            occupantPhotos: updatedPhotos,
        };

        // Update both local state and parent
        setValue('occupantPhotos', updatedPhotos);
        setFormData(newFormData);

        // Reset input
        e.target.value = '';
    };

    const handleRemovePhoto = (index: number) => {
        // Remove the photo at the specific index
        const updatedPhotos = [...(formValues.occupantPhotos || [])];
        updatedPhotos[index] = undefined;

        // Also clear the photoPath from the occupant data if it exists
        const updatedOccupants = [...(formValues.occupants || [])];
        if (updatedOccupants[index]) {
            updatedOccupants[index] = {
                ...updatedOccupants[index],
                photoPath: undefined
            };
        }

        // Update both local state and parent
        setValue('occupantPhotos', updatedPhotos);
        setValue('occupants', updatedOccupants);
        setFormData({
            ...formValues,
            occupantPhotos: updatedPhotos,
            occupants: updatedOccupants,
        });
    };

    // Handle file preview click for occupant photos
    const handlePhotoPreview = (file: File) => {
        openPreviewWithData(file, file.name);
    };

    // Handle existing photo preview click — pass filename with extension so dialog shows image
    const handleExistingPhotoPreview = (photoPath: string, _occupantName: string) => {
        const filenameWithExt = photoPath?.split('/').pop() ?? 'photo.png';
        openPreview(photoPath, filenameWithExt);
    };

    // Add effect to log form data changes
    // useEffect(() => {
    //     console.log('Form data updated:', {
    //         occupantPhotos: formValues.occupantPhotos?.map(f => f?.name),
    //         occupantsCount: formValues.occupants?.length,
    //     });
    // }, [formValues.occupantPhotos, formValues.occupants]);

    return (
        <Box display='flex'
    flexDirection='column'
    gap={3}
    width="100%"
    maxWidth="100%"
    >
            {/* Address Section */}
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Controller
                    name='ward'
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            variant='filled'
                            sx={{
                                flex: '1 1 45%',
                                maxWidth: isMobile ? '100%' : '45%',
                                '& .MuiFilledInput-root': { backgroundColor: '#FEFBFF' },
                                // Override disabled styles to maintain normal appearance
                                '& .MuiFilledInput-root.Mui-disabled': {
                                    backgroundColor: '#FEFBFF',
                                    '& fieldset': {
                                        borderColor: '#E0E0E0',
                                    },
                                    '& input': {
                                        color: '#000000',
                                        WebkitTextFillColor: '#000000',
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-disabled': {
                                    color: '#6B7280',
                                },
                            }}
                            error={!!errors.ward}
                            disabled={loading || !isEditable}
                        >
                            <InputLabel
                                sx={{
                                    color: '#6B7280',
                                    fontSize: 13,
                                    '&.Mui-focused': { color: '#2D4677' },
                                }}
                                required
                            >
                                {loading ? 'Loading...' : 'Ward No'}
                            </InputLabel>
                            <Select {...field}>
                                {wards.map((ward) => (
                                    <MenuItem key={ward.id} value={ward.ward_number}>
                                        {ward.ward_number}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    name='erfNo'
                    control={control}
                    render={({ field, fieldState }) => (
                        <InputField
                            label='Erf No'
                            required
                            fullWidth
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            {...field}
                            disabled={!isEditable}
                            slotProps={
                                tenantRules.erfNoMaxDigits != null
                                    ? {
                                          htmlInput: {
                                              maxLength: tenantRules.erfNoMaxDigits,
                                              inputMode: 'numeric' as const,
                                              pattern: '[0-9]*',
                                          },
                                      }
                                    : undefined
                            }
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}

            <Divider sx={{ my: 2, borderColor: '#F1F1F1' }} />

            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Controller
                    name='address1'
                    control={control}
                    render={({ field }) => (
                        <InputField 
                            label='Address line 1' 
                            required
                            fullWidth 
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller
                    name='address2'
                    control={control}
                    render={({ field }) => (
                        <InputField 
                            label='Address line 2' 
                            fullWidth 
                            {...field}
                            onChange={(e) => {
                                const formattedValue = formatTextWithCapitalization(e.target.value);
                                field.onChange(formattedValue);
                            }}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
            </Box>

            <Controller
                name='address3'
                control={control}
                render={({ field }) => (
                    <InputField 
                        label='Address line 3' 
                        fullWidth
                        {...field}
                        onChange={(e) => {
                            const formattedValue = formatTextWithCapitalization(e.target.value);
                            field.onChange(formattedValue);
                        }}
                        disabled={!isEditable}
                        sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                    />
                )}
            />

            <Controller
                name='town'
                control={control}
                render={({ field }) => (
                    <FormControl
                        variant='filled'
                        sx={{
                            flex: '1 1 45%',
                            maxWidth: isMobile ? '100%' : '45%',
                            '& .MuiFilledInput-root': { backgroundColor: '#FEFBFF' },
                            // Override disabled styles to maintain normal appearance
                            '& .MuiFilledInput-root.Mui-disabled': {
                                backgroundColor: '#FEFBFF',
                                '& fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                                '& input': {
                                    color: '#000000',
                                    WebkitTextFillColor: '#000000',
                                },
                            },
                            '& .MuiInputLabel-root.Mui-disabled': {
                                color: '#6B7280',
                            },
                        }}
                        error={!!errors.town}
                        disabled={loading || !isEditable}
                    >
                        <InputLabel
                            sx={{
                                color: '#6B7280',
                                fontSize: 13,
                                '&.Mui-focused': { color: '#2D4677' },
                            }}
                            required
                        >
                            {loading ? 'Loading...' : 'Town'}
                        </InputLabel>
                        <Select {...field}>
                            {towns.map((town) => (
                                <MenuItem key={town.id} value={town.town}>
                                    {town.town}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            />

            <Divider sx={{ my: 2, borderColor: '#F1F1F1' }} />

            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Controller
                    name='accountNumber'
                    control={control}
                    render={({ field, fieldState }) => (
                        <InputField
                            label='Account Number'
                            required={tenantRules.accountNumberRequired}
                            placeholder='e.g., 123456789'
                            fullWidth
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            {...field}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
                <Controller
                    name='electricityMeterNo'
                    control={control}
                    render={({ field, fieldState }) => (
                        <InputField
                            label='Electricity Meter No'
                            required={tenantRules.electricityMeterRequired}
                            placeholder='e.g., 07123456789'
                            fullWidth
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            {...field}
                            disabled={!isEditable}
                            sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                        />
                    )}
                />
            </Box>

            {/* Occupant Section */}
            <Divider sx={{ borderColor: '#F1F1F1' }} />
            <Typography
                sx={{
                    fontWeight: 600,
                    color: '#132F5F',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '22px',
                }}
            >
                Occupant Details
            </Typography>

            {fields.map((field, index) => (
                <Box key={field.id} display='flex' flexDirection='column' gap={2}>
                    <Box 
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 2,
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Controller
                            name={`occupants.${index}.name`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <InputField
                                    fullWidth
                                    label='First Name'
                                    required
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    {...field}
                                    onChange={(e) => {
                                        const formattedValue = formatTextWithCapitalization(e.target.value);
                                        field.onChange(formattedValue);
                                    }}
                                    disabled={!isEditable}
                                    sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                                />
                            )}
                        />
                        <Controller
                            name={`occupants.${index}.surname`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <InputField
                                    fullWidth
                                    label='Last Name'
                                    required
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    {...field}
                                    onChange={(e) => {
                                        const formattedValue = formatTextWithCapitalization(e.target.value);
                                        field.onChange(formattedValue);
                                    }}
                                    disabled={!isEditable}
                                    sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                                />
                            )}
                        />
                    </Box>

                    <Controller
                        name={`occupants.${index}.personalId`}
                        control={control}
                        render={({ field, fieldState }) => (
                            <InputField
                                fullWidth
                                label='Personal ID'
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                {...field}
                                disabled={!isEditable}
                                sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                            />
                        )}
                    />

                    <Box 
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 2,
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Controller
                            name={`occupants.${index}.employerName`}
                            control={control}
                            render={({ field }) => (
                                <InputField 
                                    fullWidth 
                                    label='Employer Name' 
                                    {...field}
                                    onChange={(e) => {
                                        const formattedValue = formatTextWithCapitalization(e.target.value);
                                        field.onChange(formattedValue);
                                    }}
                                    disabled={!isEditable}
                                    sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                                />
                            )}
                        />
                        <Controller
                            name={`occupants.${index}.monthlyIncome`}
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    fullWidth
                                    required
                                    label='Monthly Income'
                                    value={field.value !== undefined && field.value !== null && field.value !== '' ? `R${field.value}` : ''}
                                    onChange={e => {
                                        const withoutPrefix = e.target.value.replace(/^R\s*/i, '');
                                        field.onChange(sanitizeMoneyInput(withoutPrefix));
                                    }}
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                    disabled={!isEditable}
                                    sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                                />
                            )}
                        />
                    </Box>

                    <Controller
                        name={`occupants.${index}.cellNumber`}
                        control={control}
                        render={({ field, fieldState: { error, isTouched } }) => (
                            <InputField
                                fullWidth
                                required={cellNumberRequired}
                                label='Cell Number'
                                error={!!error}
                                helperText={error?.message}
                                {...field}
                                disabled={!isEditable}
                                sx={{ flex: '1 1 46%', maxWidth: isMobile?'100%':'46%' }}
                            />
                        )}
                    />

                    <Divider sx={{ my: 1, borderColor: '#F1F1F1' }} />

                    <Box
                        sx={{
                            backgroundColor: '#FEFBFF',
                            px: 2,
                            py: 1.2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid gray',
                            minHeight: 48,
                            flex: '1 1 46%',
                            maxWidth: isMobile ? '100%' : '46%',
                        }}
                    >
                        <Box>
                            <Typography variant='body2' mb={0.5} fontSize={16} fontWeight={400}>
                                Upload Photo
                            </Typography>
                            {formValues.occupantPhotos?.[index] ? (
                                <Typography
                                    variant='caption'
                                    sx={{
                                        color: '#2D4677',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={() => {
                                        const photo = formValues.occupantPhotos?.[index];
                                        if (photo) {
                                            handlePhotoPreview(photo);
                                        }
                                    }}
                                >
                                    <AttachFileIcon sx={{ fontSize: 16 }} />
                                    {formValues.occupantPhotos[index].name}
                                    <Button
                                        variant='text'
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering the parent click
                                            handleRemovePhoto(index);
                                        }}
                                        sx={{
                                            minWidth: 'auto',
                                            p: 0,
                                            color: '#FF544B',
                                            fontSize: 12,
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </Typography>
                            ) : formValues.occupants?.[index]?.photoPath ? (
                                <Typography
                                    variant='caption'
                                    sx={{
                                        color: '#2D4677',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={() => {
                                        const occupant = formValues.occupants?.[index];
                                        if (occupant?.photoPath) {
                                            handleExistingPhotoPreview(occupant.photoPath, occupant.name);
                                        }
                                    }}
                                >
                                    <AttachFileIcon sx={{ fontSize: 16 }} />
                                    Existing Photo:{' '}
                                    {formValues.occupants[index]?.photoPath?.split('/').pop()}
                                    <Typography
                                        variant='caption'
                                        color='textSecondary'
                                        sx={{ fontStyle: 'italic' }}
                                    >
                                        (from server)
                                    </Typography>
                                </Typography>
                            ) : (
                                <Typography variant='caption' color='textSecondary'>
                                    Max size: {MAX_FILE_SIZE_MB}MB. Allowed types:{' '}
                                    {ALLOWED_FILE_TYPES.join(', ')}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            component='label'
                            variant='text'
                            disabled={!isEditable}
                            sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#0A1D56',
                                p: 0,
                                textDecoration: 'underline',
                                minWidth: 0,
                                // Override disabled styles to maintain normal appearance
                                '&.Mui-disabled': {
                                    color: '#0A1D56',
                                    opacity: 1,
                                },
                            }}
                            endIcon={<AttachFileIcon sx={{ fontSize: 20 }} />}
                        >
                            Upload
                            <input
                                hidden
                                type='file'
                                accept='image/*,.pdf'
                                onChange={e => handleFileChange(index, e)}
                                disabled={!isEditable}
                            />
                        </Button>
                    </Box>

                    <Divider sx={{ my: 1, borderColor: '#F1F1F1' }} />

                    <Box display='flex' gap={2} mt={2}>
                        <Button
                            variant='outlined'
                            startIcon={<Trash2 color='#FF544B' />}
                            onClick={() => remove(index)}
                            disabled={!isEditable}
                            sx={{
                                textTransform: 'none',
                                width: isMobile ? '100%' : '200px',
                                height: '56px',
                                borderRadius: '12px',
                                fontWeight: '500',
                                fontSize: '14px',
                                fontFamily: 'Poppins, sans-serif',
                                color: '#132F5F',
                                // Override disabled styles to maintain normal appearance
                                '&.Mui-disabled': {
                                    color: '#132F5F',
                                    borderColor: '#132F5F',
                                    opacity: 1,
                                },
                            }}
                        >
                            Delete Occupant
                        </Button>
                    </Box>
                </Box>
            ))}

            <Button
                variant='outlined'
                startIcon={<PlusCircleIcon color='#FF544B' height={20} width={20} />}
                onClick={handleAddOccupant}
                disabled={!isEditable}
                sx={{
                    alignSelf: isMobile ? 'stretch' : 'flex-start',
                    borderRadius: '12px',
                    height: '56px',
                    width: isMobile ? '100%' : '200px',
                    fontWeight: '500',
                    fontSize: '16px',
                    color: '#132F5F',
                    textTransform: 'none',
                    // Override disabled styles to maintain normal appearance
                    '&.Mui-disabled': {
                        color: '#132F5F',
                        borderColor: '#132F5F',
                        opacity: 1,
                    },
                }}
            >
                Add Occupant
            </Button>

            {/* File Preview Dialog */}
            <FilePreviewDialog
                open={isOpen}
                onClose={closePreview}
                filePath={filePath}
                fileData={fileData || undefined}
                filename={filename}
            />
        </Box>
    );
});

StepAddressServices.displayName = 'StepAddressServices';

export default StepAddressServices;
